import React, { useEffect, useRef, useState, useCallback } from 'react';

import type { ModalProps } from './types';

/**
 * Modal — vinculante destrutivo com requireConfirmText opcional.
 *
 * Pattern DS v2.2.0 — primitivo universal para diálogos.
 * destructive=true: botão vermelho + typing-to-confirm.
 * requireConfirmText: destravar confirmar só após digitar texto exato.
 *
 * Acessibilidade:
 * - role="dialog" + aria-modal="true" + aria-labelledby
 * - Esc fecha (exceto persistent=true)
 * - Focus trap dentro do modal
 * - Tab cycling between interactive elements
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  destructive = false,
  requireConfirmText,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  onConfirm,
  persistent = false,
  maxWidth = '480px',
}: ModalProps) {
  const [typedConfirm, setTypedConfirm] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`);

  const hasTypingConfirm = destructive && !!requireConfirmText;
  const confirmEnabled = !hasTypingConfirm || typedConfirm === requireConfirmText;

  const handleClose = useCallback(() => {
    if (!persistent) {
      setTypedConfirm('');
      onClose();
    }
  }, [persistent, onClose]);

  const handleConfirm = useCallback(() => {
    if (!confirmEnabled) return;
    setTypedConfirm('');
    onConfirm?.();
  }, [confirmEnabled, onConfirm]);

  // Esc to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTypedConfirm('');
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus first element on open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusable = modalRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-4, 16px)',
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    maxWidth,
    backgroundColor: 'var(--bg-surface, #fff)',
    borderRadius: 'var(--radius-xl, 12px)',
    boxShadow: 'var(--shadow-lg, 0 20px 60px rgba(0,0,0,0.2))',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-5, 20px) var(--space-5, 20px) var(--space-4, 16px)',
    borderBottom: '1px solid var(--border-default, #e5e7eb)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 'var(--text-base, 14px)',
    fontWeight: 'var(--fw-semibold, 600)',
    color: destructive ? 'var(--danger-fg, #dc2626)' : 'var(--fg-1, #111)',
    margin: 0,
  };

  const bodyStyle: React.CSSProperties = {
    padding: 'var(--space-5, 20px)',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 'var(--space-2, 8px)',
    padding: 'var(--space-4, 16px) var(--space-5, 20px)',
    borderTop: '1px solid var(--border-default, #e5e7eb)',
  };

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    height: 32,
    padding: '0 var(--space-4, 16px)',
    fontSize: 'var(--text-sm, 12px)',
    fontWeight: 'var(--fw-medium, 500)',
    borderRadius: 'var(--radius-md, 7px)',
    cursor: 'pointer',
    border: '1px solid var(--border-default, #e5e7eb)',
    background: 'var(--bg-surface, #fff)',
    color: 'var(--fg-1, #111)',
  };

  const btnConfirmStyle: React.CSSProperties = {
    ...btnBase,
    border: 'none',
    fontWeight: 'var(--fw-semibold, 600)',
    background: confirmEnabled
      ? destructive
        ? 'var(--danger-bg, #dc2626)'
        : 'var(--brand, #d97706)'
      : 'var(--bg-subtle, #f9fafb)',
    color: confirmEnabled ? 'var(--fg-on-accent, #fff)' : 'var(--fg-muted, #9ca3af)',
    cursor: confirmEnabled ? 'pointer' : 'not-allowed',
    opacity: confirmEnabled ? 1 : 0.7,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-2, 8px) var(--space-3, 12px)',
    fontSize: 'var(--text-sm, 12px)',
    border: '1px solid var(--border-default, #e5e7eb)',
    borderRadius: 'var(--radius-sm, 5px)',
    outline: 'none',
    fontFamily: 'var(--font-sans, system-ui)',
    color: 'var(--fg-1, #111)',
    background: 'var(--bg-canvas, #fff)',
    marginTop: 'var(--space-3, 12px)',
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      data-testid="modal-overlay"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        style={containerStyle}
        data-testid="modal"
      >
        {/* Header */}
        <div style={headerStyle}>
          <h2 id={titleId.current} style={titleStyle}>
            {title}
          </h2>
          {!persistent && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Fechar"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--fg-3, #6b7280)',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-sm, 5px)',
              }}
              data-testid="modal-close"
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {children}

          {hasTypingConfirm && (
            <div style={{ marginTop: 'var(--space-4, 16px)' }}>
              <p
                style={{
                  fontSize: 'var(--text-sm, 12px)',
                  color: 'var(--fg-2, #374151)',
                  marginBottom: 'var(--space-1, 4px)',
                }}
              >
                Para confirmar, digite:{' '}
                <strong
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--danger-fg, #dc2626)',
                  }}
                >
                  {requireConfirmText}
                </strong>
              </p>
              <input
                type="text"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder={requireConfirmText}
                style={inputStyle}
                aria-label={`Digite "${requireConfirmText}" para confirmar`}
                data-testid="modal-confirm-input"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={handleClose} style={btnBase} data-testid="modal-cancel">
            {cancelLabel}
          </button>
          {onConfirm !== undefined && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!confirmEnabled}
              aria-disabled={!confirmEnabled}
              style={btnConfirmStyle}
              data-testid="modal-confirm"
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
